import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Paperclip, Eye, X } from 'lucide-react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { identityService, institutionService, licenseService } from '../../services/api';
import { fetchAllPages } from '../../utils/apiPagination';
import { personDisplayName, shortId } from '../../utils/lookupLabels';
import FileViewerModal from './FileViewerModal';
import { userFacingError } from '../../i18n/userFacingError';

export const COND_SITUACAO_OPTIONS = [
  { value: '1', label: 'Não iniciada' },
  { value: '2', label: 'Pendente' },
  { value: '3', label: 'Em atendimento' },
  { value: '4', label: 'Atendida' },
  { value: '5', label: 'Dispensada' },
];

const COND_TRANSITIONS = {
  1: ['2', '3', '5'],
  2: ['3', '4', '5'],
  3: ['2', '4', '5'],
  4: ['2'],
  5: ['2'],
};

function situacaoOptionsFor(current) {
  const allowed = new Set([String(current || '2'), ...(COND_TRANSITIONS[String(current)] || [])]);
  return COND_SITUACAO_OPTIONS.filter((o) => allowed.has(o.value));
}

function newLocalKey() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function nextCondicionanteNumero(list) {
  let max = 0;
  for (const c of list) {
    if (c._deleted) continue;
    const n = Number(c.numero);
    if (!Number.isNaN(n) && n > max) max = n;
  }
  return max + 1;
}

export function condicionanteFromApi(c) {
  return {
    localKey: c.id,
    id: c.id,
    numero: c.numero,
    condicionante: c.condicionante || '',
    situacao: c.situacao != null ? String(c.situacao) : '2',
    validade: c.validade ? String(c.validade).slice(0, 10) : '',
    data_atendimento: c.data_atendimento ? String(c.data_atendimento).slice(0, 10) : '',
    informacao: c.informacao || '',
    responsavel_id: c.responsavel_id || '',
    pendingFiles: [],
    _deleted: false,
    _dirty: false,
  };
}

export function condicionanteFromPdf(c, index) {
  return {
    localKey: newLocalKey(),
    id: null,
    numero: c.ordem ?? index + 1,
    condicionante: c.texto || '',
    situacao: '2',
    validade: '',
    data_atendimento: '',
    informacao: '',
    responsavel_id: '',
    pendingFiles: [],
    _deleted: false,
    _dirty: true,
  };
}

function AnexosList({ condicionanteId, pendingFiles, onAddPending, onRemovePending, readOnly }) {
  const [anexos, setAnexos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewerAnexo, setViewerAnexo] = useState(null);

  const loadAnexos = useCallback(async () => {
    if (!condicionanteId) return;
    setLoading(true);
    try {
      const r = await licenseService.listCondicionanteAnexos(condicionanteId);
      const d = r.data;
      setAnexos(Array.isArray(d) ? d : d?.data ?? []);
    } catch {
      setAnexos([]);
    } finally {
      setLoading(false);
    }
  }, [condicionanteId]);

  useEffect(() => {
    loadAnexos();
  }, [loadAnexos]);

  const handleFileInput = async (ev) => {
    const files = Array.from(ev.target.files || []);
    ev.target.value = '';
    if (!files.length) return;
    if (condicionanteId) {
      setUploading(true);
      try {
        for (const f of files) {
          await licenseService.uploadCondicionanteAnexo(condicionanteId, f);
        }
        toast.success(files.length > 1 ? 'Anexos enviados' : 'Anexo enviado');
        await loadAnexos();
      } catch (e) {
        toast.error(userFacingError(e, 'uploadError'));
      } finally {
        setUploading(false);
      }
    } else {
      onAddPending(files);
    }
  };

  const handleDeleteSaved = async (anexoId) => {
    if (!window.confirm('Remover este anexo?')) return;
    try {
      await licenseService.deleteCondicionanteAnexo(anexoId);
      toast.success('Anexo removido');
      await loadAnexos();
    } catch (e) {
      toast.error(userFacingError(e, 'deleteError'));
    }
  };

  return (
    <div className="mt-2 space-y-2">
      {loading ? <p className="text-xs text-gray-500">Carregando anexos…</p> : null}
      <ul className="space-y-1">
        {anexos.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-2 rounded border border-gray-100 bg-gray-50 px-2 py-1.5 text-xs">
            <span className="min-w-0 truncate font-medium text-gray-800">{a.filename_original}</span>
            <div className="flex shrink-0 items-center gap-1">
              <button type="button" className="btn btn-outline btn-xs" onClick={() => setViewerAnexo(a)}>
                <Eye className="h-3 w-3" />
              </button>
              {!readOnly ? (
                <button type="button" className="btn btn-outline btn-xs text-red-700" onClick={() => handleDeleteSaved(a.id)}>
                  <Trash2 className="h-3 w-3" />
                </button>
              ) : null}
            </div>
          </li>
        ))}
        {(pendingFiles || []).map((f, i) => (
          <li key={`pending-${i}`} className="flex items-center justify-between gap-2 rounded border border-dashed border-primary-200 bg-primary-50/50 px-2 py-1.5 text-xs">
            <span className="min-w-0 truncate text-gray-700">{f.name} (pendente)</span>
            {!readOnly ? (
              <button type="button" className="text-red-600 hover:text-red-800" onClick={() => onRemovePending(i)}>
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {!readOnly ? (
        <label className="btn btn-outline btn-xs inline-flex cursor-pointer items-center gap-1">
          <Paperclip className="h-3 w-3" />
          {uploading ? 'Enviando…' : 'Anexar arquivo'}
          <input type="file" multiple className="hidden" disabled={uploading} onChange={handleFileInput} />
        </label>
      ) : null}
      {viewerAnexo ? <FileViewerModal anexo={viewerAnexo} onClose={() => setViewerAnexo(null)} /> : null}
    </div>
  );
}

export function useCondicionanteServidorOptions(enabled = true) {
  const { data: servidores = [] } = useQuery(
    ['condicionante-servidores'],
    () => fetchAllPages((params) => institutionService.getServidores(params)),
    { enabled, staleTime: 5 * 60 * 1000 }
  );
  const { data: persons = [] } = useQuery(
    ['condicionante-persons'],
    () => fetchAllPages((params) => identityService.getPersons(params)),
    { enabled, staleTime: 5 * 60 * 1000 }
  );
  return useMemo(() => {
    const personMap = {};
    for (const p of persons) personMap[p.id] = personDisplayName(p) || shortId(p.id);
    return (servidores || []).map((s) => ({
      id: s.id,
      label: personMap[s.person_id] || s.matricula || shortId(s.id),
    }));
  }, [persons, servidores]);
}

/** Formulário de uma condicionante (listagem, modal ou editor da licença). */
export function CondicionanteForm({ item, onChange, servidorOptions = [], readOnly = false }) {
  const update = (patch) => onChange({ ...item, ...patch });
  const fieldId = item.localKey || item.id || 'novo';
  return (
    <div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="form-group md:col-span-2">
          <label className="form-label form-label-required" htmlFor={`cond-texto-${fieldId}`}>
            Texto da condicionante
          </label>
          <textarea
            id={`cond-texto-${fieldId}`}
            className="form-input min-h-[72px]"
            value={item.condicionante || ''}
            readOnly={readOnly}
            required
            onChange={(e) => update({ condicionante: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Número</label>
          <input
            type="number"
            min={1}
            className="form-input"
            value={item.numero}
            readOnly={readOnly}
            onChange={(e) => update({ numero: Number(e.target.value) || 1 })}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Situação</label>
          <select
            className="form-select"
            value={item.situacao}
            disabled={readOnly}
            onChange={(e) => {
              const situacao = e.target.value;
              const patch = { situacao };
              if ((situacao === '4' || situacao === '5') && !item.data_atendimento) {
                patch.data_atendimento = new Date().toISOString().slice(0, 10);
              }
              update(patch);
            }}
          >
            {situacaoOptionsFor(item.situacao).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Responsável</label>
          <select
            className="form-select"
            value={item.responsavel_id || ''}
            disabled={readOnly}
            onChange={(e) => update({ responsavel_id: e.target.value })}
          >
            <option value="">Sem responsável</option>
            {servidorOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Validade</label>
          <input
            type="date"
            className="form-input"
            value={item.validade || ''}
            readOnly={readOnly}
            onChange={(e) => update({ validade: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Data de atendimento</label>
          <input
            type="date"
            className="form-input"
            value={item.data_atendimento || ''}
            readOnly={readOnly}
            onChange={(e) => update({ data_atendimento: e.target.value })}
          />
        </div>
        <div className="form-group md:col-span-2">
          <label className="form-label">Informação / observação</label>
          <input
            className="form-input"
            value={item.informacao || ''}
            readOnly={readOnly}
            onChange={(e) => update({ informacao: e.target.value })}
          />
        </div>
      </div>
      <div className="mt-2 border-t border-gray-100 pt-2">
        <p className="mb-1 text-xs font-medium text-gray-600">Anexos</p>
        <AnexosList
          condicionanteId={item.id}
          pendingFiles={item.pendingFiles}
          readOnly={readOnly}
          onAddPending={(files) => update({ pendingFiles: [...(item.pendingFiles || []), ...files] })}
          onRemovePending={(i) =>
            update({ pendingFiles: (item.pendingFiles || []).filter((_, idx) => idx !== i) })
          }
        />
      </div>
    </div>
  );
}

/**
 * Editor de condicionantes para formulários de licença (criação e edição).
 */
export default function CondicionantesEditor({ value, onChange, licencaId, readOnly = false }) {
  const items = Array.isArray(value) ? value.filter((c) => !c._deleted) : [];
  const servidorOptions = useCondicionanteServidorOptions(!readOnly);

  const updateItem = (localKey, patch) => {
    onChange(
      value.map((c) =>
        c.localKey === localKey ? { ...c, ...patch, _dirty: c.id ? true : c._dirty } : c
      )
    );
  };

  const addItem = () => {
    const numero = nextCondicionanteNumero(value);
    onChange([
      ...value,
      {
        localKey: newLocalKey(),
        id: null,
        numero,
        condicionante: '',
        situacao: '2',
        validade: '',
        data_atendimento: '',
        informacao: '',
        responsavel_id: '',
        pendingFiles: [],
        _deleted: false,
        _dirty: true,
      },
    ]);
  };

  const removeItem = (localKey) => {
    onChange(
      value.map((c) => {
        if (c.localKey !== localKey) return c;
        if (c.id) return { ...c, _deleted: true };
        return null;
      }).filter(Boolean)
    );
  };

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-gray-500">Nenhuma condicionante. Adicione manualmente ou importe de um PDF.</p>
      ) : (
        items.map((c) => (
          <div key={c.localKey} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-start justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                Condicionante nº {c.numero}
              </span>
              {!readOnly ? (
                <button
                  type="button"
                  className="text-red-600 hover:text-red-800"
                  onClick={() => removeItem(c.localKey)}
                  title="Remover condicionante"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <CondicionanteForm
              item={c}
              readOnly={readOnly}
              servidorOptions={servidorOptions}
              onChange={(next) => updateItem(c.localKey, next)}
            />
          </div>
        ))
      )}
      {!readOnly ? (
        <button type="button" className="btn btn-outline btn-sm inline-flex items-center gap-2" onClick={addItem}>
          <Plus className="h-4 w-4" />
          Adicionar condicionante
        </button>
      ) : null}
    </div>
  );
}

/**
 * Sincroniza condicionantes com a API após salvar a licença.
 */
export async function syncCondicionantes(licencaId, drafts) {
  const list = Array.isArray(drafts) ? drafts : [];
  const toDelete = list.filter((c) => c._deleted && c.id);
  const active = list.filter((c) => !c._deleted);

  for (const c of toDelete) {
    await licenseService.deleteCondicionante(c.id);
  }

  for (const c of active) {
    const payload = {
      licenca_id: licencaId,
      numero: c.numero,
      condicionante: c.condicionante.trim(),
      situacao: c.situacao || '2',
      validade: c.validade || null,
      data_atendimento: c.data_atendimento || null,
      informacao: c.informacao?.trim() || null,
      responsavel_id: c.responsavel_id || null,
    };
    let condId = c.id;
    if (c.id) {
      if (c._dirty) {
        await licenseService.updateCondicionante(c.id, payload);
      }
    } else {
      const res = await licenseService.createCondicionante(payload);
      condId = res?.data?.id ?? res?.id;
    }
    if (condId && c.pendingFiles?.length) {
      for (const f of c.pendingFiles) {
        await licenseService.uploadCondicionanteAnexo(condId, f);
      }
    }
  }
}
